import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Search, Plus, Edit, Trash2, Volume2, Users, Play, X, Key } from 'lucide-react';
import { apiService, TextToSpeechProvider, TtsVoice, TtsModel, VoicePreviewRequest, VoicePreviewResponse } from '../../services/api';

/** Format a stored price for display: empty if undefined, otherwise at least 2 decimals. */
function formatPriceMin2(n?: number): string {
  if (n == null) return '';
  const decimals = String(n).includes('.') ? String(n).split('.')[1].length : 0;
  return n.toFixed(Math.max(2, decimals));
}

export function TextToSpeechPage() {
  const [providers, setProviders] = useState<TextToSpeechProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingProvider, setViewingProvider] = useState<TextToSpeechProvider | null>(null);
  const [editingProvider, setEditingProvider] = useState<TextToSpeechProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    providerType: 'elevenlabs' as 'elevenlabs' | 'azure' | 'google' | 'custom',
    baseUrl: '',
    voices: [] as TtsVoice[],
    models: [] as TtsModel[],
    configSchema: '{}',
    status: 'active' as 'active' | 'inactive' | 'deprecated',
    defaultApiKey: '',
  });
  const [newVoice, setNewVoice] = useState<TtsVoice>({
    id: '',
    name: '',
    gender: 'neutral' as 'male' | 'female' | 'neutral',
    language: 'en',
    description: '',
    preview_text: ''
  });
  const [newModel, setNewModel] = useState<{ name: string; displayName: string; description: string; pricePerMinuteEur: string }>({
    name: '',
    displayName: '',
    description: '',
    pricePerMinuteEur: '',
  });
  // Index-aligned with formData.models — holds the raw editable price string so
  // a saved 0.20 stays "0.20" instead of being normalized to "0.2" by the number input.
  const [modelPriceDrafts, setModelPriceDrafts] = useState<string[]>([]);
  const [_previewText, _setPreviewText] = useState('');
  const [_playingVoice, _setPlayingVoice] = useState<string | null>(null);

  const providerTypes = [
    { value: 'elevenlabs', label: 'ElevenLabs' },
    { value: 'azure', label: 'Azure Speech' },
    { value: 'google', label: 'Google Cloud TTS' },
    { value: 'custom', label: 'Custom Provider' }
  ];

  // Removed _voiceCategories as it's not used

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTextToSpeechProviders();
      setProviders(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der TTS-Provider');
      console.error('Error loading TTS providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProviders = providers.filter(provider =>
    provider.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Lade TTS-Provider...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{typeof error === 'string' ? error : (error?.message || 'An error occurred')}</div>
        <Button onClick={loadProviders} className="ml-4">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  const handleEdit = (provider: TextToSpeechProvider) => {
    setEditingProvider(provider);
    const models = [...(provider.models ?? [])];
    setFormData({
      name: provider.name,
      displayName: provider.displayName,
      providerType: provider.providerType,
      baseUrl: provider.baseUrl || '',
      voices: [...provider.voices],
      models,
      configSchema: JSON.stringify(provider.configSchema, null, 2),
      status: provider.status,
      defaultApiKey: '', // Leave empty as we don't display existing keys for security
    });
    setModelPriceDrafts(models.map((m) => formatPriceMin2(m.pricePerMinuteEur)));
    setViewingProvider(null);
    setIsDialogOpen(true);
  };

  const handleAddVoice = () => {
    if (newVoice.id && newVoice.name) {
      setFormData({
        ...formData,
        voices: [...formData.voices, { ...newVoice }]
      });
      setNewVoice({ id: '', name: '', gender: 'neutral', language: 'en', description: '', preview_text: '' });
    }
  };

  const handleRemoveVoice = (index: number) => {
    setFormData({
      ...formData,
      voices: formData.voices.filter((_, i) => i !== index)
    });
  };

  const handleAddModel = () => {
    if (newModel.name && newModel.displayName) {
      const trimmedPrice = newModel.pricePerMinuteEur.trim();
      const parsedPrice = trimmedPrice === '' ? undefined : Number(trimmedPrice);
      if (parsedPrice !== undefined && (Number.isNaN(parsedPrice) || parsedPrice < 0)) {
        alert('Preis pro Minute muss eine nicht-negative Zahl sein.');
        return;
      }
      const model: TtsModel = {
        name: newModel.name,
        displayName: newModel.displayName,
        ...(newModel.description && { description: newModel.description }),
        ...(parsedPrice !== undefined && { pricePerMinuteEur: parsedPrice }),
      };
      setFormData({ ...formData, models: [...formData.models, model] });
      setModelPriceDrafts([...modelPriceDrafts, formatPriceMin2(parsedPrice)]);
      setNewModel({ name: '', displayName: '', description: '', pricePerMinuteEur: '' });
    }
  };

  const handleRemoveModel = (index: number) => {
    setFormData({ ...formData, models: formData.models.filter((_, i) => i !== index) });
    setModelPriceDrafts(modelPriceDrafts.filter((_, i) => i !== index));
  };

  const handleUpdateModelPrice = (index: number, value: string) => {
    const trimmed = value.trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);
    if (parsed !== undefined && (Number.isNaN(parsed) || parsed < 0)) return;
    setModelPriceDrafts(modelPriceDrafts.map((d, i) => (i === index ? value : d)));
    setFormData({
      ...formData,
      models: formData.models.map((m, i) => (i === index ? { ...m, pricePerMinuteEur: parsed } : m)),
    });
  };

  const handleBlurModelPrice = (index: number) => {
    setModelPriceDrafts(modelPriceDrafts.map((d, i) => (i === index ? formatPriceMin2(formData.models[i]?.pricePerMinuteEur) : d)));
  };

  const handlePreviewVoice = async (voice: TtsVoice) => {
    if (!formData.defaultApiKey && !(editingProvider?.hasDefaultApiKey)) {
      alert('Bitte geben Sie zuerst einen Standard API-Schlüssel ein oder stellen Sie sicher, dass ein Standard API-Schlüssel konfiguriert ist.');
      return;
    }

    try {
      let config: Record<string, any> = {};
      if (formData.configSchema) {
        config = JSON.parse(formData.configSchema);
      }

      const previewRequest: VoicePreviewRequest = {
        provider: formData.name || formData.providerType,
        voiceId: voice.id,
        text: voice.preview_text || 'Dies ist eine Vorschau dieser Stimme.',
        config: config
      };

      console.log('Preview request:', previewRequest);

      const response: VoicePreviewResponse = await apiService.previewTextToSpeechVoice(previewRequest);
      
      // Create audio element and play
      const audio = new Audio(response.audioData);
      await audio.play();
    } catch (error) {
      console.error('Error previewing voice:', error);
      alert('Fehler beim Erstellen der Sprachvorschau: ' + (error as Error).message);
    }
  };

  const handleSave = async () => {
    try {
      const configObj = JSON.parse(formData.configSchema);
      
      const providerData = {
        name: formData.name,
        displayName: formData.displayName,
        providerType: formData.providerType,
        baseUrl: formData.baseUrl,
        voices: formData.voices,
        models: formData.models,
        configSchema: configObj,
        status: formData.status,
        ...(formData.defaultApiKey && { defaultApiKey: formData.defaultApiKey })
      };

      if (editingProvider) {
        await apiService.updateTextToSpeechProvider(editingProvider.id, providerData);
      } else {
        await apiService.createTextToSpeechProvider(providerData);
      }

      await loadProviders();
      closeDialog();
    } catch (error) {
      console.error('Error saving provider:', error);
      alert('Fehler beim Speichern des Providers');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Sind Sie sicher, dass Sie diesen Provider löschen möchten?')) {
      try {
        await apiService.deleteTextToSpeechProvider(id);
        await loadProviders();
      } catch (error) {
        console.error('Error deleting provider:', error);
        alert('Fehler beim Löschen des Providers');
      }
    }
  };

  const toggleActive = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    try {
      const newStatus: 'active' | 'inactive' | 'deprecated' = provider.status === 'active' ? 'inactive' : 'active';
      const updateData = {
        name: provider.name,
        displayName: provider.displayName,
        providerType: provider.providerType,
        baseUrl: provider.baseUrl,
        voices: provider.voices,
        configSchema: provider.configSchema,
        status: newStatus
      };
      await apiService.updateTextToSpeechProvider(id, updateData);
      await loadProviders();
    } catch (error) {
      console.error('Error toggling provider status:', error);
      alert('Fehler beim Ändern des Provider-Status');
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingProvider(null);
    setViewingProvider(null);
    setFormData({
      name: '',
      displayName: '',
      providerType: 'elevenlabs',
      baseUrl: '',
      voices: [],
      models: [],
      configSchema: '{}',
      status: 'active',
      defaultApiKey: '',
    });
    setNewVoice({ id: '', name: '', gender: 'neutral', language: 'en', description: '', preview_text: '' });
    setNewModel({ name: '', displayName: '', description: '', pricePerMinuteEur: '' });
    setModelPriceDrafts([]);
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      'elevenlabs': 'bg-purple-100 text-purple-800',
      'azure': 'bg-blue-100 text-blue-800',
      'google': 'bg-green-100 text-green-800',
      'custom': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'elevenlabs': 'ElevenLabs',
      'azure': 'Azure Speech',
      'google': 'Google Cloud TTS',
      'custom': 'Custom Provider'
    };
    return labels[type] || type;
  };

  const getCategoryBadgeColor = (gender: string) => {
    const colors: Record<string, string> = {
      'male': 'bg-blue-100 text-blue-800',
      'female': 'bg-pink-100 text-pink-800',
      'neutral': 'bg-gray-100 text-gray-800'
    };
    return colors[gender] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (gender: string) => {
    const labels: Record<string, string> = {
      'male': 'Männlich',
      'female': 'Weiblich',
      'neutral': 'Neutral'
    };
    return labels[gender] || gender;
  };

  // Removed handleVoicePreview function as it references non-existent API methods

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Text-to-Speech Provider</h1>
          <p className="text-gray-600">Verwaltung der Sprachsynthese-Anbieter</p>
        </div>
        <Button onClick={() => { setViewingProvider(null); setEditingProvider(null); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Neuer Provider
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Provider suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProviders.map((provider) => (
          <Card key={provider.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Volume2 className="w-5 h-5 text-green-600" />
                    <CardTitle className="text-lg">{provider.displayName}</CardTitle>
                    {provider.hasDefaultApiKey && (
                      <div className="flex items-center">
                        <Key className="w-4 h-4 text-green-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge className={getTypeBadgeColor(provider.providerType)}>
                      {getTypeLabel(provider.providerType)}
                    </Badge>
                    <Badge variant={provider.status === 'active' ? 'default' : 'secondary'}>
                      {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                </div>
                <Switch
                  checked={provider.status === 'active'}
                  onCheckedChange={() => toggleActive(provider.id)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Verfügbare Stimmen ({provider.voices.length})
                  </h4>
                  <div className="space-y-2">
                    {provider.voices.slice(0, 3).map((voice, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{voice.name}</div>
                          <div className="text-xs text-gray-600">{voice.description}</div>
                        </div>
                        {voice.gender && (
                          <Badge className={getCategoryBadgeColor(voice.gender)} variant="outline">
                            {getCategoryLabel(voice.gender)}
                          </Badge>
                        )}
                      </div>
                    ))}
                    {provider.voices.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{provider.voices.length - 3} weitere Stimmen
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{provider.voices?.length || 0} Stimmen</span>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500">
                  Erstellt: {new Date(provider.createdAt).toLocaleDateString('de-DE')}
                </div>
                
                <div className="flex items-center space-x-2 pt-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(provider.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProviders.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Keine Provider gefunden.</p>
            <Button className="mt-4" onClick={() => { setViewingProvider(null); setEditingProvider(null); setIsDialogOpen(true); }}>
              Ersten Provider erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingProvider ? 'Provider Details' :
               editingProvider ? 'Provider bearbeiten' : 'Neuen Provider erstellen'}
            </DialogTitle>
            <DialogDescription>
              {viewingProvider ? 'Detaillierte Ansicht des Text-to-Speech Providers' :
               'Konfigurieren Sie einen Text-to-Speech Provider'}
            </DialogDescription>
          </DialogHeader>

          {viewingProvider ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Provider Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {viewingProvider.name}</div>
                      <div><strong>Anzeigename:</strong> {viewingProvider.displayName}</div>
                      <div><strong>Typ:</strong> 
                        <Badge className={`ml-2 ${getTypeBadgeColor(viewingProvider.providerType)}`}>
                          {getTypeLabel(viewingProvider.providerType)}
                        </Badge>
                      </div>
                      <div><strong>Status:</strong> {viewingProvider.status === 'active' ? 'Aktiv' : 'Inaktiv'}</div>
                      <div><strong>URL:</strong> <code className="text-xs">{viewingProvider.baseUrl || 'N/A'}</code></div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Verfügbare Stimmen ({viewingProvider.voices.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewingProvider.voices.map((voice, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-gray-900">{voice.name}</div>
                          {voice.gender && (
                            <Badge className={getCategoryBadgeColor(voice.gender)} variant="outline">
                              {getCategoryLabel(voice.gender)}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-1">{voice.id}</div>
                        <div className="text-sm text-gray-600">{voice.description}</div>
                        {voice.preview_text && (
                          <div className="text-xs text-gray-500 mt-1 italic">
                            Preview: "{voice.preview_text}"
                          </div>
                        )}
                        <div className="flex items-center mt-2">
                          <Button variant="ghost" size="sm" className="h-6 px-2">
                            <Play className="w-3 h-3 mr-1" />
                            Test
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Konfiguration</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-gray-800">{JSON.stringify(viewingProvider.configSchema, null, 2)}</pre>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Schließen
                </Button>
                <Button onClick={() => handleEdit(viewingProvider)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">Allgemein</TabsTrigger>
                <TabsTrigger value="models">Modelle</TabsTrigger>
                <TabsTrigger value="voices">Stimmen</TabsTrigger>
                <TabsTrigger value="configuration">Konfiguration</TabsTrigger>
              </TabsList>
              
              <div className="mt-6">
                <TabsContent value="general" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name (technisch)</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="z.B. openai_tts"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Anzeigename</Label>
                      <Input
                        id="displayName"
                        value={formData.displayName}
                        onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                        placeholder="z.B. OpenAI TTS"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="type">Typ</Label>
                      <Select value={formData.providerType} onValueChange={(value: 'elevenlabs' | 'azure' | 'google' | 'custom') => setFormData({...formData, providerType: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Typ wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {providerTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="status"
                        checked={formData.status === 'active'}
                        onCheckedChange={(checked) => setFormData({...formData, status: checked ? 'active' : 'inactive'})}
                      />
                      <Label htmlFor="status">Provider aktiv</Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="url">API URL</Label>
                    <Input
                      id="url"
                      value={formData.baseUrl}
                      onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                      placeholder="https://api.example.com/v1/text-to-speech"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="defaultApiKey">Standard API-Schlüssel</Label>
                      {editingProvider?.hasDefaultApiKey && (
                        <div className="flex items-center gap-1 text-green-600">
                          <Key className="h-4 w-4" />
                          <span className="text-xs">API Key gesetzt</span>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Input
                        id="defaultApiKey"
                        type="password"
                        value={formData.defaultApiKey || ''}
                        onChange={(e) => setFormData({...formData, defaultApiKey: e.target.value})}
                        placeholder="Geben Sie den Standard API-Schlüssel ein"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setFormData({...formData, defaultApiKey: ''})}
                        disabled={!formData.defaultApiKey && !(editingProvider?.hasDefaultApiKey)}
                        className="shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600">
                      Wenn leer, wird der aktuelle Wert in der Datenbank beibehalten
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="models" className="space-y-4 mt-0">
                  <div className="flex items-center justify-between">
                    <Label>Verfügbare TTS-Modelle</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddModel}>
                      <Plus className="w-4 h-4 mr-2" />
                      Modell hinzufügen
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Modelle werden vom Telephony-Service verwendet (z.B. <code>eleven_flash_v2_5</code>, <code>gpt-4o-mini-tts</code>).
                    Der Preis pro Minute wird für die Admin-Kostenübersicht verwendet. Wenn kein Modell auf dem Agent gesetzt ist,
                    wird das Modell mit niedrigstem <code>ranking</code> als Default verwendet.
                  </p>

                  <div className="grid grid-cols-4 gap-2 p-3 border border-dashed border-gray-300 rounded-lg">
                    <Input
                      placeholder="Modell Name (z.B. eleven_flash_v2_5)"
                      value={newModel.name}
                      onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                    />
                    <Input
                      placeholder="Anzeigename"
                      value={newModel.displayName}
                      onChange={(e) => setNewModel({ ...newModel, displayName: e.target.value })}
                    />
                    <Input
                      placeholder="Beschreibung"
                      value={newModel.description}
                      onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
                    />
                    <Input
                      type="number"
                      step="0.000001"
                      min="0"
                      placeholder="€/Min (z.B. 0.18)"
                      value={newModel.pricePerMinuteEur}
                      onChange={(e) => setNewModel({ ...newModel, pricePerMinuteEur: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    {formData.models.map((model, index) => (
                      <div key={index} className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">{model.displayName}</div>
                          <div className="text-sm text-gray-600">{model.name}</div>
                          {model.description && (
                            <div className="text-xs text-gray-500">{model.description}</div>
                          )}
                        </div>
                        <div className="flex flex-col w-40">
                          <Label className="text-xs text-gray-500 mb-1">€/Min</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="z.B. 0.18"
                            value={modelPriceDrafts[index] ?? ''}
                            onChange={(e) => handleUpdateModelPrice(index, e.target.value)}
                            onBlur={() => handleBlurModelPrice(index)}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveModel(index)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {formData.models.length === 0 && (
                      <div className="text-center text-sm text-gray-500 py-4">
                        Noch keine Modelle definiert.
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="voices" className="space-y-4 mt-0">
                  <div className="flex items-center justify-between">
                    <Label>Verfügbare Stimmen</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddVoice}>
                      <Plus className="w-4 h-4 mr-2" />
                      Stimme hinzufügen
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 p-4 border border-dashed border-gray-300 rounded-lg">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Stimme ID"
                        value={newVoice.id}
                        onChange={(e) => setNewVoice({...newVoice, id: e.target.value})}
                      />
                      <Input
                        placeholder="Name"
                        value={newVoice.name}
                        onChange={(e) => setNewVoice({...newVoice, name: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={newVoice.gender} onValueChange={(value: 'male' | 'female' | 'neutral') => setNewVoice({...newVoice, gender: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Geschlecht" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Männlich</SelectItem>
                          <SelectItem value="female">Weiblich</SelectItem>
                          <SelectItem value="neutral">Neutral</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Sprache (z.B. en, de)"
                        value={newVoice.language}
                        onChange={(e) => setNewVoice({...newVoice, language: e.target.value})}
                      />
                    </div>
                    <Input
                      placeholder="Beschreibung"
                      value={newVoice.description}
                      onChange={(e) => setNewVoice({...newVoice, description: e.target.value})}
                    />
                    <div>
                      <Label htmlFor="preview_text" className="text-sm font-medium">
                        Preview Text (max 500 Zeichen)
                      </Label>
                      <Textarea
                        id="preview_text"
                        placeholder="Hallo, dies ist eine Beispielaufnahme dieser Stimme. Vielen Dank fürs Zuhören."
                        value={newVoice.preview_text || ''}
                        onChange={(e) => {
                          const text = e.target.value;
                          if (text.length <= 500) {
                            setNewVoice({...newVoice, preview_text: text});
                          }
                        }}
                        className="min-h-[60px] text-sm"
                        maxLength={500}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {(newVoice.preview_text || '').length}/500 Zeichen
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {formData.voices.map((voice, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="font-medium">{voice.name}</div>
                            {voice.gender && (
                              <Badge className={getCategoryBadgeColor(voice.gender)} variant="outline">
                                {getCategoryLabel(voice.gender)}
                              </Badge>
                            )}
                            {voice.language && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                {voice.language}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{voice.id}</div>
                          <div className="text-xs text-gray-500">{voice.description}</div>
                          {voice.preview_text && (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              Preview: "{voice.preview_text}"
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-1">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handlePreviewVoice(voice)}
                            disabled={!formData.defaultApiKey && !(editingProvider?.hasDefaultApiKey)}
                            className="text-blue-600"
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveVoice(index)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="configuration" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label htmlFor="configuration">Konfiguration (JSON)</Label>
                    <p className="text-sm text-gray-600">
                      Definieren Sie die JSON-Schema-Konfiguration für diesen Provider. Dies wird verwendet, um die verfügbaren Einstellungen und deren Standardwerte zu definieren.
                    </p>
                    <Textarea
                      id="configuration"
                      value={formData.configSchema}
                      onChange={(e) => setFormData({...formData, configSchema: e.target.value})}
                      placeholder='{"api_key": "API_KEY", "model": "tts-1", "voice": "alloy", "response_format": "mp3"}'
                      className="min-h-[300px] font-mono text-sm"
                    />
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Beispiel-Konfiguration:</h4>
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap">
{JSON.stringify({
  "stability": {
    "type": "number",
    "default": 0.5,
    "min": 0,
    "max": 1,
    "description": "Voice stability"
  },
  "similarity_boost": {
    "type": "number", 
    "default": 0.8,
    "min": 0,
    "max": 1,
    "description": "Similarity boost"
  }
}, null, 2)}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </div>

              <div className="flex justify-end space-x-2 mt-6 pt-6 border-t">
                <Button variant="outline" onClick={closeDialog}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave}>
                  {editingProvider ? 'Speichern' : 'Erstellen'}
                </Button>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}